<template>

    <section class="overflow-hidden relative px-4 xl:px-0">

        <div class="hidden absolute right-0 -top-96 -mt-12 lg:block">
            <nuxt-img sizes="xs:325px sm:400px md:612px lg:612px xl:612px" format="webp" loading="lazy" :src="topRightImageSrc" width="612" height="935" :alt="topRightImageAlt" class="" />
        </div>

        <div class="absolute -left-52 -bottom-80 md:-left-6">
            <nuxt-img sizes="xs:325px sm:400px md:429px lg:429px xl:429px" format="webp" loading="lazy" :src="bottomLeftImageSrc" width="429" height="963" :alt="bottomLeftImageAlt" class="" />
        </div>

         <div class="relative z-10 mx-auto mt-9 w-full md:mt-16">
            <button @click="scrollTo(scrollTarget)"
            class="flex gap-x-2 items-center px-24 py-4 mx-auto text-xl rounded-full transition-colors duration-200 md:text-2xl md:px-32 lg:py-6 font-ww-medium btn-red">{{ buttonText }}</button>


           <div class="mt-2 text-base text-center font-ww-regular">
            {{ limitedSuppliesText }}
           </div>

          </div> 


        <div class="py-8 mx-auto max-w-4xl md:py-16">

            <div>
                <div
                    class="text-2xl leading-8 text-center xl:!leading-9 md:text-3xl xl:text-32 font-ww-medium xl:pr-16 md:text-left"
                    v-html="introHeading">
                </div>

                <div
                    class="mt-4 text-base !leading-6 md:mt-8 xl:text-lg font-ww-regular xl:pr-20 text-left"
                    v-html="introText">
                </div>

                <div class="mt-12 mb-6 text-3xl text-center md:text-4xl font-ww-bold xl:text-42 md:text-left"
                    v-html="mainHeading">
                </div>

                <div class="grid grid-cols-12 md:gap-x-16">

                    <div class="order-2 col-span-12 md:col-span-6 md:order-1">
                        <div class="text-base !leading-6 xl:text-lg font-ww-regular text-left"
                            v-html="mainContent">
                        </div>
                    </div>

                    <div
                        class="relative order-1 col-span-12 pb-24 md:col-span-6 md:order-2 md-max:w-fit md-max:mx-auto md:pb-0">

                        <div>
                            <nuxt-img sizes="xs:293px sm:371px md:329px lg:371px xl:371" format="webp" loading="lazy" :src="contentImage1Src" width="371" height="345" :alt="contentImage1Alt"
                                class="md-max:mx-auto" />
                        </div>

                        <div class="absolute top-12">
                            <nuxt-img sizes="xs:293px sm:371px md:329px lg:371px xl:371" format="webp" loading="lazy" :src="contentImage2Src" width="414" height="423" :alt="contentImage2Alt"
                                class="md-max:mx-auto" />
                        </div>


                    </div>


                </div>
            </div>

        </div>

    </section>


</template>

<script setup>

const props = defineProps({
  topRightImageSrc: {
    type: String,
    default: '/assets/images/img-decaf-3.svg',
  },
  topRightImageAlt: {
    type: String,
    default: 'Decaf Coffee',
  },
  bottomLeftImageSrc: {
    type: String,
    default: '/assets/images/img-decaf-4.svg',
  },
  bottomLeftImageAlt: {
    type: String,
    default: 'Decaf Coffee',
  },
  introHeading: {
    type: String,
    default: "If you love coffee but hate the jitters, sleepless nights, or that uneasy feeling in your stomach after a cup… you're not alone.",
  },
  introText: {
    type: String,
    default: "That's exactly why tens of thousands of Americans are making the switch to our decaf — a smooth, rich, and full-flavored brew that's 99.9% caffeine-free and completely free of harmful chemicals. It's crafted for clean energy, gut health, and peace of mind — without compromising on taste. You'd never guess it's decaf.",
  },
  mainHeading: {
    type: String,
    default: 'The Dirty Truth About Decaf Coffees',
  },
  mainContent: {
    type: String,
    default: `Most people assume decaf coffee is simply regular coffee with the caffeine removed. But what they don't know is how that caffeine is stripped away — and how it may be harming their health.
            <br><br>
            The shocking truth? The majority of decaf coffees are processed using harsh chemical solvents like methylene chloride or ethyl acetate — substances also used in paint removers and industrial cleaners. These residues can linger in your cup, exposing you to toxins with every sip.
            <br><br>
            And as if that weren't bad enough, this chemical process doesn't just remove caffeine — it also strips away the flavor, aroma, and natural health benefits of the coffee bean. That's why most decaf tastes flat, bitter, and bland… leaving you disappointed and wondering if you'll ever find a decaf that actually satisfies.`,
  },
  contentImage1Src: {
    type: String,
    default: '/assets/images/img-decaf-1.png',
  },
  contentImage1Alt: {
    type: String,
    default: 'Decaf Coffee',
  },
  contentImage2Src: {
    type: String,
    default: '/assets/images/img-decaf-2.png',
  },
  contentImage2Alt: {
    type: String,
    default: 'Decaf Coffee',
  },
  buttonText: {
    type: String,
    default: 'Get Discount',
  },
  scrollTarget: {
    type: String,
    default: 'products',
  },
  limitedSuppliesText: {
    type: String,
    default: 'Limited supplies available. Special discount available today.',
  },
});

defineBuilderComponent({
  friendlyName: 'TextImageSplit',
  inputs: [
    {
      name: 'topRightImageSrc',
      type: 'file',
      friendlyName: 'Top Right Background Image',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
      defaultValue: '/assets/images/img-decaf-3.svg',
    },
    {
      name: 'topRightImageAlt',
      type: 'text',
      friendlyName: 'Top Right Image Alt Text',
      defaultValue: 'Decaf Coffee',
    },
    {
      name: 'bottomLeftImageSrc',
      type: 'file',
      friendlyName: 'Bottom Left Background Image',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
      defaultValue: '/assets/images/img-decaf-4.svg',
    },
    {
      name: 'bottomLeftImageAlt',
      type: 'text',
      friendlyName: 'Bottom Left Image Alt Text',
      defaultValue: 'Decaf Coffee',
    },
    {
      name: 'introHeading',
      type: 'richText',
      friendlyName: 'Intro Heading',
      defaultValue: "If you love coffee but hate the jitters, sleepless nights, or that uneasy feeling in your stomach after a cup… you're not alone.",
    },
    {
      name: 'introText',
      type: 'richText',
      friendlyName: 'Intro Text',
      defaultValue: "That's exactly why tens of thousands of Americans are making the switch to our decaf — a smooth, rich, and full-flavored brew that's 99.9% caffeine-free and completely free of harmful chemicals. It's crafted for clean energy, gut health, and peace of mind — without compromising on taste. You'd never guess it's decaf.",
    },
    {
      name: 'mainHeading',
      type: 'richText',
      friendlyName: 'Main Heading',
      defaultValue: 'The Dirty Truth About Decaf Coffees',
    },
    {
      name: 'mainContent',
      type: 'richText',
      friendlyName: 'Main Content',
      defaultValue: `Most people assume decaf coffee is simply regular coffee with the caffeine removed. But what they don't know is how that caffeine is stripped away — and how it may be harming their health.
            <br><br>
            The shocking truth? The majority of decaf coffees are processed using harsh chemical solvents like methylene chloride or ethyl acetate — substances also used in paint removers and industrial cleaners. These residues can linger in your cup, exposing you to toxins with every sip.
            <br><br>
            And as if that weren't bad enough, this chemical process doesn't just remove caffeine — it also strips away the flavor, aroma, and natural health benefits of the coffee bean. That's why most decaf tastes flat, bitter, and bland… leaving you disappointed and wondering if you'll ever find a decaf that actually satisfies.`,
    },
    {
      name: 'contentImage1Src',
      type: 'file',
      friendlyName: 'Content Image 1',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/img-decaf-1.png',
    },
    {
      name: 'contentImage1Alt',
      type: 'text',
      friendlyName: 'Content Image 1 Alt Text',
      defaultValue: 'Decaf Coffee',
    },
    {
      name: 'contentImage2Src',
      type: 'file',
      friendlyName: 'Content Image 2',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/img-decaf-2.png',
    },
    {
      name: 'contentImage2Alt',
      type: 'text',
      friendlyName: 'Content Image 2 Alt Text',
      defaultValue: 'Decaf Coffee',
    },
    {
      name: 'buttonText',
      type: 'text',
      friendlyName: 'Button Text',
      defaultValue: 'Get Discount',
    },
    {
      name: 'scrollTarget',
      type: 'text',
      friendlyName: 'Scroll Target Section ID',
      defaultValue: 'products',
    },
    {
      name: 'limitedSuppliesText',
      type: 'text',
      friendlyName: 'Limited Supplies Text',
      defaultValue: 'Limited supplies available. Special discount available today.',
    },
  ],
});
</script>